import {
  createElement,
  CSSProperties,
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsRTL } from '../../../locale';
import { DesignTokens, renderInlineCssVars, useDesignTokens } from '../../../theme';
import { getScrollbarSize } from '../../../util/dom/getScrollbarSize';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import { Icon } from '../../Icon';
import { TablePosition, TableRegions } from '../../Table';
import { useListState } from '../useListState';
import { Defaults } from '../../Table/__internal__/Defaults';
import {
  BaseTable,
  defaultSizes,
  hasSelectionColumn,
  RenderRowProps,
} from '../../Table/__internal__/internal';
import { GroupHeaderItem } from '../GroupHeaderItem';
import { Item } from '../Item';
import { BaseItem } from '../Item/__internal__/BaseItem';
import { LinkItem } from '../LinkItem';
import { ListIconColumnProps } from '../';
import { useListContext } from './';
import { useGetInfoCell } from './hooks/ListNarration/useGetInfoCell';
import { useSayCell } from './hooks/ListNarration/useSayCell';
import { useRefArray } from '../../../hooks/useRefArray';
import { BaseListColumnProps, BaseItemType, BaseListIconColumnProps, BaseListProps } from './types';
import { getCellHelpPopup, getCellIcon } from './utils/utils';
import { IconHelpPopup, IconHelpPopupAPIRef } from './IconHelpPopup';
import cx from 'classnames';
import cnButton from '../../Button/__internal__/BaseButton.css.json';
import cnGroupHeaderItem from '../GroupHeaderItem/GroupHeaderItem.css.json';
import cnItem from '../Item/Item.css.json';
import cn from '../List.css.json';
import '../List.css';

// Minimum size needed for an icon button, normal icons will be centered.
const compactWidth = 32;

// Add space to the last cell for the scrollbar, this mimics what Table does when
// cell borders are not visible
const paddingInlineEnd = getScrollbarSize() + 1;

/**
 * @internal
 */
export const BaseList = (props: BaseListProps) => {
  const selectionMode = !props.disabled ? props.selectionMode ?? 'none' : 'none';
  // pass a 1 to hasSelectionColumn for column count since List always has a column
  // (it doesn't matter if there are more columns...just need to know there's at least 1)
  const hasSelectionCol = hasSelectionColumn(selectionMode, 1, props.rowCount);
  const startIndex = hasSelectionCol ? 1 : 0;
  const currentPosition = useRef<TablePosition | undefined>(props.initialFocus);
  const [isActive, setIsActive] = useState(false);
  const [rowIndexPressed, setRowIndexPressed] = useState<number | null>(null);
  const dt = useDesignTokens();
  const direction = useIsRTL() ? 'rtl' : 'ltr';
  const getItemProps = (rowIndex: number) => {
    if (props.rowCount === 0 || rowIndex === -1 || rowIndex >= props.rowCount) return null;

    return (
      props.getItemInfo?.(rowIndex)?.props ??
      props.renderItem?.({
        index: rowIndex,
      })?.props ??
      itemColumn?.item?.(rowIndex)?.props
    );
  };
  const getItemGroup = (rowIndex: number): string => {
    if (props.rowCount === 0 || rowIndex === -1 || rowIndex >= props.rowCount) return '';
    return getItemProps(rowIndex)?.group ?? '';
  };

  const listContext = useListContext();

  const iconColumnsWidth = props.iconColumns
    ? props.iconColumns.length * compactWidth +
      (listContext.tableHasVerticalScrollbar ? paddingInlineEnd : 0)
    : 0;
  const listWidth = props.width ?? defaultSizes.tableWidth;
  const selectionColumnWidth = startIndex ? defaultSizes.selectionControlColumnWidth : 0;
  const itemColumnWidth = listWidth - selectionColumnWidth - iconColumnsWidth;

  const getRowIdentifier = useCallback(
    (rowIndex: number): string => {
      const rowKey = props.getRowKey;
      return rowKey ? rowKey(rowIndex) : Defaults.getRowKey(rowIndex);
    },
    [props.getRowKey]
  );

  const itemColumn: BaseListColumnProps = useMemo(() => {
    // Table columns set the default maximum width to 1000. Since we only want the item column to resize
    // we're overriding the max column width to make sure the icon columns aren't resized.
    const column: BaseListColumnProps = props.itemColumn
      ? { ...props.itemColumn, width: itemColumnWidth, maximumWidth: 99999 }
      : {
          key: 'itemColumn',
        };
    if (!props.renderItem) {
      column.cellContent = (_columnIndex, rowIndex, _columnKey, ref, disabled, readOnly) =>
        column.item?.(rowIndex, ref, disabled, readOnly) ?? null;
    }
    // pass a 1 to hasSelectionColumn for column count since List always has a column
    // (it doesn't matter if there are more columns...just need to know there's at least 1)
    if (hasSelectionColumn(props.selectionMode, 1, props.rowCount) && !props.disabled) {
      column.style = {
        paddingInlineStart: 'var(--list-spacing-s)',
        paddingInlineEnd:
          !props.iconColumns || props.iconColumns?.length === 0
            ? 'calc(var(--table-last-column-pd-adjustment) + var(--list-spacing-s))'
            : 'var(--list-spacing-s)',
        ...column.style,
      };
    }

    return column;
  }, [
    props.disabled,
    props.itemColumn,
    props.renderItem,
    props.selectionMode,
    props.iconColumns,
    props.rowCount,
    itemColumnWidth,
  ]);

  const getItemType = (rowIndex: number): BaseItemType => {
    if (props.rowCount === 0 || rowIndex === -1 || rowIndex >= props.rowCount) return 'invalid';

    const type =
      props.getItemInfo?.(rowIndex)?.type ??
      props.renderItem?.({
        index: rowIndex,
      })?.type ??
      itemColumn?.item?.(rowIndex)?.type;

    if (type === Item || type === BaseItem) {
      return 'item';
    } else if (type === LinkItem) {
      return 'linkItem';
    } else if (type === GroupHeaderItem) {
      return 'groupItem';
    }
    return 'custom';
  };

  if (currentPosition?.current) {
    // Unlike Table, the List does not have header so it's never -1. Otherwise,
    // -1 may have the focus disappeared.
    currentPosition.current.rowIndex = Math.max(
      0,
      Math.min(currentPosition.current.rowIndex, props.rowCount - 1)
    );
  }

  const defaultTableRef = useRef<HTMLDivElement>(null);
  const tableRef = props.tableRef ?? defaultTableRef;

  const {
    refs: helpPopupAPIRefs,
    createSetRef: createHelpPopupAPIRef,
  } = useRefArray<IconHelpPopupAPIRef | null>();

  const {
    refs: helpPopupButtonRefs,
    createSetRef: createHelpPopupButtonRef,
  } = useRefArray<HTMLButtonElement | null>();

  const iconColumns = useMemo(() => {
    // sorting non-interactive icon columns on the left and interactive icon
    // columns, including HellPopup, on the right
    const order = (col: ListIconColumnProps) => (col.cellHelpPopup ? 2 : col.onPress ? 1 : 0);
    const iconColumns = (props.iconColumns ? [...props.iconColumns] : [])
      .sort((iconColumn1, iconColumn2) => order(iconColumn1) - order(iconColumn2))
      .map((column, i) => {
        const isLastColumn = i === (props.iconColumns?.length ?? 0) - 1;
        const onPress = column.onPress;
        const col: BaseListIconColumnProps = {
          ...column,
          width:
            compactWidth +
            (isLastColumn && listContext.tableHasVerticalScrollbar ? paddingInlineEnd : 0),
          minimumWidth:
            compactWidth +
            (isLastColumn && listContext.tableHasVerticalScrollbar ? paddingInlineEnd : 0),
          alignment: 'center',
          preventToggleSelection: true,
          style: {
            paddingInlineStart: '0',
            paddingInlineEnd: isLastColumn ? 'var(--table-last-column-pd-adjustment)' : '0',
          },
          // only override onPress if used
          onPress: onPress
            ? (rowIndex, columnKey) => {
                if (!col.disabled?.(rowIndex)) onPress?.(rowIndex, columnKey);
              }
            : undefined,
          cellContent: (columnIndex, rowIndex, _columnKey, _ref, disabled, readOnly) => {
            const cellHelpPopupProps = getCellHelpPopup(col, rowIndex);

            if (
              readOnly &&
              ((!cellHelpPopupProps && onPress) ||
                (cellHelpPopupProps && !col?.displayHelpPopupReadOnly?.(rowIndex)))
            )
              return null;

            if (cellHelpPopupProps)
              return (
                <IconHelpPopup
                  buttonRef={(button) => {
                    setRef(col.helpPopupButtonRef?.(rowIndex), button);
                    setRef(createHelpPopupButtonRef?.(rowIndex), button);
                  }}
                  ref={createHelpPopupAPIRef(rowIndex)}
                  testId={postfixTestId(col.testId, `row${rowIndex}-helpPopup`)}
                  disabled={disabled || col.disabled?.(rowIndex)}
                  {...cellHelpPopupProps}
                  className={cx(
                    isActive &&
                      currentPosition.current?.rowIndex === rowIndex &&
                      currentPosition.current?.colIndex === columnIndex + startIndex
                      ? cnButton.active
                      : undefined,
                    cellHelpPopupProps.className
                  )}
                />
              );

            const cellIcon = getCellIcon(col, rowIndex);
            if (!cellIcon) {
              // null will prevent selection not showing so
              // using the following to have something empty,
              // which will show the selection background
              return <Fragment />;
            }

            return onPress ? (
              <Button
                ref={(e) => setRef(col.iconButtonRef?.(rowIndex), e)}
                testId={postfixTestId(col.testId, `row${rowIndex}-cellIcon`)}
                icon={cellIcon}
                tooltip={cellIcon.tooltip}
                ariaLabel={cellIcon.ariaLabel}
                variant={ButtonVariant.Icon}
                className={
                  isActive &&
                  currentPosition.current?.rowIndex === rowIndex &&
                  currentPosition.current?.colIndex === columnIndex + startIndex
                    ? cnButton.active
                    : undefined
                }
                disabled={disabled || col.disabled?.(rowIndex)}
                tokens={
                  props.tokens?.theme
                    ? (state) => {
                        switch (state) {
                          case 'hover':
                            return { fg: 'var(--list-item-hover-fg)' };
                          default:
                            return { fg: 'var(--list-item-fg)' };
                        }
                      }
                    : undefined
                }
              />
            ) : (
              <Icon
                {...cellIcon}
                className={cn.iconColumnStaticIcon}
                disabled={disabled || col.disabled?.(rowIndex)}
                tokens={
                  props.tokens?.theme ? () => ({ 'default-fg': 'var(--list-item-fg)' }) : undefined
                }
              />
            );
          },
        };

        return col;
      });
    return iconColumns;
  }, [
    props.iconColumns,
    props.tokens?.theme,
    listContext.tableHasVerticalScrollbar,
    isActive,
    startIndex,
    createHelpPopupButtonRef,
    createHelpPopupAPIRef,
  ]);

  const columns = [itemColumn, ...iconColumns];

  const listInfoCell = useGetInfoCell({
    getItemType,
    getItemProps,
    accessibilityInfo: props.accessibilityInfo,
    helpPopupButtonRefs,
    readOnly: props.readOnly,
    isItemReadOnly: props.isItemReadOnly,
    getRowKey: getRowIdentifier,
  });
  const listSayCell = useSayCell({
    columns,
    startColIndex: startIndex,
    getItemType,
    getItemGroup,
    disabled: props.disabled,
    isItemDisabled: props.isItemDisabled,
    narration: props.narration,
    readOnly: props.readOnly,
    isItemReadOnly: props.isItemReadOnly,
    getRowKey: getRowIdentifier,
  });

  // using onActivate is not supported when using link items
  const onActivate =
    getItemType(currentPosition.current?.rowIndex ?? 0) === 'linkItem'
      ? undefined
      : props.onActivate;

  let tokens = {
    ...renderInlineCssVars(
      {
        m: dt.spacing.core.m,
        s: dt.spacing.core.s,
        xs: dt.spacing.core.xs,
        xxs: dt.spacing.core.xxs,
        l: dt.spacing.core.l,
        'squish-s': dt.spacing.insetSquish.s.padding,
      },
      'list-spacing'
    ),
    ...renderInlineCssVars(
      {
        ...getListItemTokens(dt),
        'disabled-fg': dt.enableNovaTokens
          ? dt.stateStandardNova.disabled.fg
          : dt.stateStandard.disabled.fg,
        // the following is also used for useItemHeight
        'font-size-m':
          props.tokens?.font?.fontSize ??
          (dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m),
        'font-size-s':
          props.tokens?.font?.fontSize ??
          (dt.enableNovaTokens ? dt.fontNova.size.s : dt.font.size.s),
        'font-size-xs':
          props.tokens?.font?.fontSize ??
          (dt.enableNovaTokens ? dt.fontNova.size.xs : dt.font.size.xs),
        'text-decoration': props.tokens?.font?.textDecoration ?? 'inherit',
        'font-weight-default':
          props.tokens?.font?.fontWeight ??
          (dt.enableNovaTokens ? dt.fontNova.weight.default : dt.font.weight.default),
        'font-weight-thick':
          props.tokens?.font?.fontWeight ??
          (dt.enableNovaTokens ? dt.fontNova.weight.thick : dt.font.weight.thick),
        'font-style': props.tokens?.font?.fontStyle ?? 'inherit',
        'font-family': props.tokens?.font?.fontFamily ?? 'inherit',
        'unread-bg': dt.enableNovaTokens ? dt.c.colorPrimary : dt.c.primary,
      },
      'list-item'
    ),
    ...renderInlineCssVars(
      {
        bd: dt.c.colorUiBorder,
        fg: props.tokens?.theme?.({ state: 'normal' })?.fg ?? dt.c.colorTextDefault,
      },
      'list-group-header'
    ),
    ...renderInlineCssVars(
      {
        'border-highlighting-color':
          dt.themeVariant === 'dark' ? dt.stateStandardNova.selected.bd : dt.c.colorNeutral,
      },
      'list-row'
    ),

    ...(props.tokens?.whiteSpace
      ? renderInlineCssVars({ 'white-space': props.tokens?.whiteSpace }, 'list-item')
      : {}),
  };
  const overrides = props.tokens?.theme;
  if (overrides) {
    tokens = {
      ...tokens,
      ...renderInlineCssVars(overrides({ state: 'normal' }), 'list-item'),
      ...renderInlineCssVars(overrides({ state: 'selected' }), 'list-item-selected'),
      ...renderInlineCssVars(overrides({ state: 'hover' }), 'list-item-hover'),
    };
    const normalTokens = overrides({ state: 'normal' });
    if (normalTokens?.fg) {
      tokens = {
        ...tokens,
        ...renderInlineCssVars({ fg: normalTokens.fg }, 'list-item-description'),
      };
    }
  }

  const tableState = useListState({
    selectedRows: props.selectedRows,
    selectAll: props.selectAll,
  });

  // custom row rendering function
  const renderListRow = (rowProps: RenderRowProps) => {
    const {
      rowIndex,
      createRow,
      createCell,
      hasSelectionControl,
      isEmpty,
      disabled,
      readOnly,
      dragging,
    } = rowProps;
    const isGroupItem = getItemType(rowIndex) === 'groupItem';
    const preference = props.preference ?? tableState.preference;
    const selectedCell =
      dragging !== true &&
      ((!disabled && preference?.selectAll === true) ||
        !!preference?.selectedRows?.has(getRowIdentifier(rowIndex)));

    // do not render an empty row...this will cause exceptions
    if (isEmpty)
      return createRow({
        children: undefined,
      });

    // construct regular rows
    const cells = [];
    let i = 0;

    if (hasSelectionControl) {
      // if this is not a group header item, add a selection cell
      !isGroupItem && cells.push(rowProps.createSelectionCell(i));
      // there is a selection column, so increment the column index
      i++;
    }

    const item =
      props.renderItem?.({ index: rowIndex, disabled, readOnly }) ?? itemColumn.cellContent;

    // create group headers for group rows
    if (isGroupItem) {
      const cell = createCell({
        index: 0,
        content: item,
        colSpan: (hasSelectionControl ? 1 : 0) + columns.length,
        className: cnGroupHeaderItem.groupItem,
      });

      // return a row with only the header cell
      return createRow({
        children: cell,
      });
    }

    // add content cell
    cells.push(
      rowProps.createCell({
        // pass in i for the column index and then increment i
        // for any additional column below
        index: i++,
        content: item,
        className: cx(cn.cell, cn.direction[direction]),
      })
    );

    // add any additional cells
    const itemColumnIndexOffset = i;
    for (i = Math.max(i, rowProps.columnStartIndex); i <= rowProps.columnStopIndex; i++) {
      // create icon button for given column
      const iconColumn = iconColumns?.[i - itemColumnIndexOffset];

      if (iconColumn) {
        // add icon button cell
        cells.push(
          rowProps.createCell({
            index: i,
            content: iconColumn.cellContent?.(
              hasSelectionControl ? i - 1 : i,
              rowIndex,
              iconColumn.key,
              undefined,
              disabled,
              readOnly
            ),
            className: cx(cn.cell, cn.direction[direction]),
          })
        );
      }
    }

    // construct a row around cell content and return to table for display
    const row = createRow({
      children: cells,
      className: cx(
        props.borderHighlighting && cn.rowSelectionBorder,
        (selectedCell || rowIndexPressed === rowIndex) &&
          props.borderHighlighting &&
          cn.rowSelectionBorderSelected
      ),
      // Don't render background styling when using borderHighlighting
      style: {
        ...(selectedCell &&
          !hasSelectionControl &&
          !props.borderHighlighting &&
          renderInlineCssVars({ 'description-fg': dt.c.colorTextDefault }, 'list-item')),
        ...((selectedCell || rowIndexPressed === rowIndex) &&
          props.borderHighlighting &&
          renderInlineCssVars(
            {
              'pressed-bg': 'transparent',
              'pressed-filter': dt.stateStandardNova.normal.filter,
              'selected-bg': 'transparent',
              'selected-filter': dt.stateStandardNova.normal.filter,
              'selected-inactive-bg': 'transparent',
              'selected-inactive-filter': dt.stateStandardNova.normal.filter,
            },
            'table'
          )),
      },
    });
    return row;
  };

  if (currentPosition?.current) {
    // the visibility of the selection column can be change via disabled so account for
    // the out of range focus position.
    currentPosition.current.colIndex = Math.max(
      0,
      Math.min(currentPosition.current.colIndex, columns.length - 1 + (hasSelectionCol ? 1 : 0))
    );
  }

  return (
    <div className={cx(cn.list, props.disabled && cnItem.disabled, props.className)} style={tokens}>
      <div
        className={cx(cn.listInner, props.className ? `${props.className}-inner` : undefined)}
        style={{
          ...props.style,
        }}
        onMouseDown={(e) => {
          const target = e.target as HTMLElement;
          const row = target.closest('[role="row"]');
          if (row) {
            const rowIndex = currentPosition.current?.rowIndex ?? 0;
            setRowIndexPressed(rowIndex);
          }
        }}
        onMouseUp={() => {
          setRowIndexPressed(null);
        }}
        onClick={(e) => {
          const rowIndex = currentPosition.current?.rowIndex ?? 0;
          const colIndex = currentPosition.current?.colIndex ?? 0;
          const rowKey = getRowIdentifier(rowIndex);
          const disabled = props.disabled || props.isItemDisabled?.(rowKey);
          const readOnly = props.readOnly || props.isItemReadOnly?.(rowKey);
          const iconColumn = iconColumns.length > 0 && iconColumns[colIndex - 1 - startIndex];

          if (disabled) return;

          if (helpPopupButtonRefs[rowIndex]?.contains(e.target as Node)) {
            if (iconColumn && iconColumn.cellHelpPopup) {
              const displayReadOnly = readOnly && iconColumn?.displayHelpPopupReadOnly?.(rowIndex);
              if (!readOnly || displayReadOnly) {
                const columnKey = iconColumn?.key || '';
                helpPopupAPIRefs[rowIndex]?.toggle?.();
                // Table does not fire onPress in readOnly so account for displayReadOnly
                if (displayReadOnly) iconColumn.onPress?.(rowIndex, columnKey);
              }
            }
          } else {
            helpPopupAPIRefs[rowIndex]?.close?.();
          }
        }}
        onKeyDown={(e) => {
          const rowIndex = currentPosition.current?.rowIndex ?? 0;
          const rowKey = getRowIdentifier(rowIndex);
          const disabled = props.disabled || props.isItemDisabled?.(rowKey);
          const readOnly = props.readOnly || props.isItemReadOnly?.(rowKey);
          const hasModifier = e.altKey || e.ctrlKey || e.shiftKey || e.metaKey;

          if (disabled || readOnly) return;

          setIsActive(
            ((e.key === 'Enter' || e.key === ' ') &&
              props.iconColumns?.some(
                (col) => (!!col.onPress || !!col.cellHelpPopup) && !col.disabled?.(rowIndex)
              )) ??
              false
          );
          if (e.key === 'Enter' || e.key === ' ') {
            setRowIndexPressed(rowIndex);
          }
          if (
            e.key === 'Enter' &&
            !hasModifier &&
            (currentPosition.current?.colIndex ?? startIndex) === startIndex
          ) {
            const itemProps = getItemProps(rowIndex);
            if (itemProps && getItemType(rowIndex) === 'linkItem' && itemProps.onPress) {
              // it's a LinkItem
              e.stopPropagation();
              e.preventDefault();
              itemProps.onPress(e);
            }
          }
        }}
        onKeyUp={(e) => {
          const rowIndex = currentPosition.current?.rowIndex ?? 0;
          const colIndex = currentPosition.current?.colIndex ?? 0;
          const rowKey = getRowIdentifier(rowIndex);
          const disabled = props.disabled || props.isItemDisabled?.(rowKey);
          const readOnly = props.readOnly || props.isItemReadOnly?.(rowKey);
          const iconColumn = iconColumns.length > 0 && iconColumns[colIndex - 1 - startIndex];
          const hasModifier = e.altKey || e.ctrlKey || e.shiftKey || e.metaKey;

          if (iconColumn && iconColumn.cellHelpPopup) {
            const displayReadOnly = readOnly && iconColumn?.displayHelpPopupReadOnly?.(rowIndex);

            if (
              !disabled &&
              (!readOnly || displayReadOnly) &&
              !hasModifier &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              const columnKey = iconColumn?.key || '';
              helpPopupAPIRefs[rowIndex]?.open?.();
              // Table does not fire onPress in readOnly so account for displayReadOnly
              if (displayReadOnly) iconColumn.onPress?.(rowIndex, columnKey);
            }
          }

          if (disabled || readOnly) return;

          if (e.key === 'Enter' || e.key === ' ') {
            setRowIndexPressed(null);
          }
          if ((e.key === 'Enter' || e.key === ' ') && isActive) {
            setIsActive(false);
          }
          if (e.key === 'Delete') {
            props.onDeletePress?.(
              tableState.preference.selectedRows,
              currentPosition.current?.rowIndex ?? 0
            );
          }
        }}
        onFocus={(e) => {
          if (e.target instanceof HTMLButtonElement && helpPopupButtonRefs.includes(e.target))
            tableRef.current?.focus();
        }}
      >
        <BaseTable
          {...tableState}
          {...props}
          tableRef={tableRef}
          style={
            {
              '--table-bg': 'transparent',
              '--scrollbar-hover-track-bg': 'transparent',
            } as CSSProperties
          }
          columns={columns}
          hideSelectedStateStyles={hasSelectionCol}
          hideHoverStateStyles={hasSelectionCol && !onActivate}
          hidePressedStateStyles={hasSelectionCol && !onActivate}
          renderRow={renderListRow}
          isRowDisabled={props.isItemDisabled}
          isRowReadOnly={props.isItemReadOnly}
          reorderRow={(fromRow, toRow, rowsToMove) =>
            props.reorderItem?.(fromRow, toRow, rowsToMove)
          }
          initialFocus={
            props.initialFocus ?? {
              rowIndex: currentPosition.current?.rowIndex ?? 0,
              colIndex: currentPosition.current?.colIndex ?? startIndex,
              region: TableRegions.Data,
            }
          }
          initialFocusVisible={props.initialFocusVisible ?? true}
          selectionMode={selectionMode}
          disableAutoFocus={props.disableAutoFocus ?? true}
          hideManageColumnsButton
          cellBordersVisible={false}
          columnHeadersVisible={false}
          frozenColumnCount={0}
          rowHeight={(rowIndex) => {
            if (getItemType(rowIndex) === 'groupItem') return 33;
            const itemHeight = props.itemHeight?.(rowIndex);
            if (typeof itemHeight === 'number') return itemHeight;
            return itemHeight === 'double' || itemHeight === 'mixed' ? 56 : 30;
          }}
          showSeparator={(index) => {
            if (props.showSeparator)
              return typeof props.showSeparator === 'function'
                ? props.showSeparator(index)
                : props.showSeparator;
            const titleMaxLines =
              getItemType(0) === 'item' ? getItemProps(0).titleMaxLines : undefined;
            return (
              props.rowCount > 0 &&
              (props.itemHeight?.(0) === 'double' ||
                props.itemHeight?.(0) === 'mixed' ||
                titleMaxLines > 1)
            );
          }}
          hideFillRows
          onFocusChange={(position: TablePosition) => {
            currentPosition.current = position;
            props.onFocusChange?.(position);
          }}
          onSelect={(selectedRows, selectAll, lastSelectedRowKey, selected) => {
            // "mixed" will never be used, not sure if it should be undefined or boolean
            props.onSelect?.(
              selectedRows,
              selectAll !== 'mixed' ? selectAll : false,
              lastSelectedRowKey,
              selected !== 'mixed' ? selected : undefined
            );
          }}
          onActivate={onActivate}
          accessibilityInfo={{
            getCellInfo: listInfoCell,
          }}
          narration={{
            onSayCell: listSayCell,
            onSaySelectionCell: listSayCell,
          }}
          ariaRoleDescription={
            props.ariaRoleDescription
              ? props.ariaRoleDescription
              : columns.length === 1 &&
                !hasSelectionCol &&
                (getItemType(0) === 'item' ||
                  (getItemType(0) === 'groupItem' && getItemType(1) === 'item'))
              ? 'listbox'
              : undefined
          }
          cellBackground={props.cellBackground ?? (() => 'var(--list-item-bg)')}
          ignoreTableOverrideTheme
        />
      </div>
    </div>
  );
};

const getListItemTokens = (dt: DesignTokens) => {
  if (!dt.enableNovaTokens) {
    switch (dt.themeVariant) {
      case 'high-contrast':
        return {
          fg: dt.c.white,
          'description-fg': dt.c.white,
        };
      case 'dark':
        return {
          fg: dt.c.white,
          'description-fg': dt.c.white80,
        };
      default:
        return {
          fg: dt.c.n_3,
          'description-fg': dt.c.n_1,
        };
    }
  } else {
    return {
      fg: dt.c.colorTextDefault,
      'description-fg': dt.c.colorTextLabel,
    };
  }
};
